import React from 'react';
import Icon from '../../../components/AppIcon';

const AchievementShowcase = ({ achievements, userAchievements, totalXP }) => {
  const earnedAchievementIds = new Set(userAchievements?.map(ua => ua?.achievement_id));

  const getAchievementTypeIcon = (type) => {
    switch (type) {
      case 'reading':
        return 'BookOpen';
      case 'quiz':
        return 'Target';
      case 'pronunciation':
        return 'Mic';
      case 'glossary':
        return 'BookText';
      case 'streak':
        return 'Flame';
      default:
        return 'Award';
    }
  };

  return (
    <div className="bg-surface-lighter border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Achievement Collection</h2>
          <p className="text-text-secondary text-sm">
            {userAchievements?.length} of {achievements?.length} achievements earned
          </p>
        </div>
        <div className="text-right">
          <p className="text-text-secondary text-sm">Total Achievement XP</p>
          <p className="text-2xl font-bold text-accent-purple">
            {userAchievements?.reduce((sum, ua) => sum + (ua?.achievements?.xp_reward || 0), 0)?.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-surface-dark rounded-full h-3 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-accent-purple to-accent-blue transition-all duration-500 rounded-full"
            style={{ width: `${(userAchievements?.length / achievements?.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Achievement Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {achievements?.slice(0, 10)?.map((achievement) => {
          const isEarned = earnedAchievementIds?.has(achievement?.id);
          const earnedData = userAchievements?.find(ua => ua?.achievement_id === achievement?.id);

          return (
            <div
              key={achievement?.id}
              className={`relative group p-4 rounded-xl border transition-all duration-300 ${
                isEarned
                  ? 'bg-gradient-to-br from-accent-purple/20 to-accent-blue/20 border-accent-purple hover:scale-105' :'bg-surface-dark border-border opacity-50 hover:opacity-75'
              }`}
            >
              {/* Achievement Icon */}
              <div className={`w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center ${
                isEarned
                  ? 'bg-gradient-to-br from-accent-purple to-accent-blue' :'bg-surface-dark border border-border'
              }`}>
                <Icon 
                  name={getAchievementTypeIcon(achievement?.achievement_type)} 
                  size={28} 
                  className={isEarned ? 'text-white' : 'text-text-secondary'}
                />
              </div>

              {/* Achievement Info */}
              <h3 className={`text-sm font-semibold text-center mb-1 ${
                isEarned ? 'text-white' : 'text-text-secondary'
              }`}>
                {achievement?.name}
              </h3>
              
              <p className="text-xs text-text-secondary text-center line-clamp-2 mb-2">
                {achievement?.description}
              </p>

              {/* XP Badge */}
              <div className={`text-center ${isEarned ? 'text-accent-purple' : 'text-text-secondary'}`}>
                <span className="text-xs font-semibold">+{achievement?.xp_reward} XP</span>
              </div>

              {/* Earned Date Tooltip */}
              {isEarned && earnedData && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-surface-dark border border-accent-purple rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Earned: {new Date(earnedData?.earned_at)?.toLocaleDateString()}
                </div>
              )}

              {/* Lock Icon for Unearned */}
              {!isEarned && (
                <div className="absolute top-2 right-2">
                  <Icon name="Lock" size={16} className="text-text-secondary" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* View All Link */}
      {achievements?.length > 10 && (
        <div className="mt-6 text-center">
          <button className="text-accent-purple hover:text-accent-blue transition-colors text-sm font-semibold">
            View All {achievements?.length} Achievements →
          </button>
        </div>
      )}
    </div>
  );
};

export default AchievementShowcase;